import { LOGGER_LOG_TYPE, BODY_TOOLTIP } from 'Config';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { TextInputController } from 'common/components/textInput/TextInput';
import { Label } from 'common/components/texts/Texts';
import InfoMessage from 'common/components/infoMessage/InfoMessage';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import CheckInput from 'common/components/checkInput/CheckInput';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { ElementTypeDto } from 'api/elementTypes/models/ElementTypeDto';
import ElementTypesService from 'api/elementTypes/ElementTypesService';
import { Form } from 'common/components/form/Form';
import styles from './ElementTypeScreen.module.scss';

function ElementTypeScreen(): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const form = useForm<ElementTypeDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['ELEMENT_TYPES_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['ELEMENT_TYPES_READ'])

    const isRecalculating = form.watch('isRecalculating');
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const url = '/library/element-types';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    useEffect(() => {
        void getData()
    }, [id, type]);

    const pollRecalculatingStatus = useCallback(async () => {
        if (!id) return;
        try {
            const result = await ElementTypesService.getById(id);
            if (!result.isRecalculating) {
                form.reset(result);
            }
        } catch { /* ignore polling errors */ }
    }, [id]);

    useEffect(() => {
        if (isRecalculating && id) {
            pollingRef.current = setInterval(() => { void pollRecalculatingStatus(); }, 10000);
        }
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [isRecalculating, id, pollRecalculatingStatus]);

    const getData = async () => {
        try {
            let result: Partial<ElementTypeDto> = { id, name: '', coefficient: 0 };
            if (id) {
                Loading.show();
                result = await ElementTypesService.getById(id);
            }
            form.reset(result);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get element type information', error);
            Toast.error(t('messages.error_load_info'));
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/library/element-types/${typeUrl}/${id}`);
        } else {
            navigate('/library/element-types');
        }
    }

    const onSubmit = async (model: ElementTypeDto) => {
        try {
            Loading.show();

            if (model && model.id) {
                await ElementTypesService.update(model)
                void getData();
            } else if (model) {
                const id = await ElementTypesService.create(model)
                navigateTo('edit', id);
            }
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the element type with id: ${id}`, error);
            Toast.error(t('messages.record_save_error'));

            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false)
            return;
        }

        Loading.show();
        try {
            await ElementTypesService.remove(form.getValues())
            Toast.success(t('messages.record_delete_success'));
            navigateTo();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete element type', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteModal(false)
        }
    }

    const entityName = form.watch('name');
    const titlePrefix = type === 'create'
        ? t('common.new_title.element_type')
        : type === 'edit'
            ? t('common.edit_title.element_type')
            : t('common.details_title.element_type');
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('element_types.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('element_types.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate>
                <PageHeader title={titlePrefix} subTitle={entityLabel} onGoBack={() => navigateTo()} informationText={t('common.go_back')}>
                    {canWrite && !isDetails && <Button type='submit'>{t('common.save')}</Button>}
                    {canWrite && isDetails && <Button fw href={`/library/element-types/edit/${id}`}>{t('common.edit')}</Button>}
                    {canWrite && isDetails && (
                        <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                            <FaTrashAlt />
                        </Button>
                    )}
                </PageHeader>
                <PageContainer>
                    <Row>
                        <Col xs={12} md={6}>
                            <Label space>{t('element_types.list.name')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='name' control={form.control}
                                placeholder={t('element_types.list.name')} disabled={isDetails}
                                rules={{ required: true, maxLength: 250 }}
                                hasError={Boolean(errors.name)} />
                            <InputError error={errors.name} maxLength={250} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>
                                {t('element_types.coefficient')}{!isDetails ? '*' : ''}
                                <OverlayTrigger
                                    placement='top'
                                    overlay={<Tooltip id='element-type-coefficient-info'>{t('element_types.coefficient_info')}</Tooltip>}
                                >
                                    <span><InfoIcon /></span>
                                </OverlayTrigger>
                            </Label>
                            <div className={styles.percentInputGroup}>
                                <TextInputController
                                    name='coefficient' control={form.control}
                                    placeholder={t('element_types.coefficient')} disabled={isDetails}
                                    type='number'
                                    rules={{ required: true }}
                                    hasError={Boolean(errors.coefficient)} />
                                <span className={styles.percentSuffix}>%</span>
                            </div>
                            <InputError error={errors.coefficient} />
                        </Col>
                    </Row>
                    <Row className='mt-3'>
                        <Col xs={12}>
                            <CheckInput
                                checked={form.watch('exportInMaterialsList') ?? false}
                                disabled={isDetails}
                                onChange={(e) => form.setValue('exportInMaterialsList', e.target.checked, { shouldDirty: true })}
                            >
                                {t('catalog_parameters.export_in_materials_list')}
                                <InfoIcon
                                    data-tooltip-id={BODY_TOOLTIP}
                                    data-tooltip-content={t('catalog_parameters.export_in_materials_list_tooltip')}
                                    style={{ marginLeft: 6, cursor: 'pointer' }}
                                />
                            </CheckInput>
                        </Col>
                    </Row>
                    {isRecalculating && (
                        <div style={{ marginTop: '1rem' }}>
                            <InfoMessage type='warning' message={t('element_types.recalculating_message')} closable={false} />
                        </div>
                    )}
                </PageContainer>
            </Form>
            <ConfirmDeleteModal
                itemName={form.getValues('name')}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    )
}

export default ElementTypeScreen;
